// Copyright (c) 2019-22 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Modal from './Modal';
import api from '../../api';
import Sortable from '../Sortable';
import { toast } from 'react-toastify';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import '../../styles/attachInfo.css';


export default class AttachInfo extends Modal {
    abortController = new AbortController();
    state = {
        providers: [],
        accounts: [],
        selected: {},
        query: "",
        editAccount: false,
        showMinimal: true,
    }

    componentDidMount = async () => {
        this.props.loading(9999);
        await this.fetchProviders();

        // Search for the account, based on the selection... 
        this.setState({
            query: this.props.data.selection,
            accounts: await this.searchAccounts(this.props.data.selection),
        });
        this.props.loaded();
    }

    fetchProviders = async () => {
        const res = await api.get('/tpa/investmentProvider/list');

        if (!res.success)
            return toast.error("Failed to get investment providers");

        const providers = res.investmentProviders;
        this.setState({
            providers
        })
    }

    searchAccounts = async (query, justPlans = false) => {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.setState({ loading: true });
        const res = await api.get(`/tpa/search?searchQuery=${query}${justPlans ? '&justPlans=true' : ''}`, { signal: this.abortController.signal }, false, {}, true);

        if (!res.success && !res.cancelled) {
            toast.error("Unable to search for accounts. Please try again later.");
            return [];
        }

        if (!res.cancelled)
            this.setState({ loading: false });

        return res?.result ?? []; // Handle the case where the request is cancelled...  
    }

    handleSubmit = async () => {

        const info = this.state.selected;
        info.planYearEnd = `${info.endMm}/${info.endDd}`

        if (!info.internalPlanId)
            return toast.error("Internal ID is required but not set for this plan.")
        if (!info.planName)
            return toast.error("Plan name is missing. Please set a name for the plan.")
        if (!info.planYearEnd)
            return toast.error("Plan year-end date is required but not set for this plan.")
        if (!info.active)
            return toast.error("Termination status needs to be set for the plan.")
        if (!info.directory)
            return toast.error("Directory information is missing for this plan.")
        if (!info.account.provider)
            return toast.error("Provider details are required for the account.")
        if (!info.account.number)
            return toast.error("Account number is missing. Please provide this detail.")
        if (!info.account.payee)
            toast.info("Payee information is not set for the account.")

        this.props.loading(9999);
        const metadata = [...[...this.props.document.metadata].filter((m) => ![
            'plan id',
            'account',
            'plan name',
            'payee',
            'provider',
            'plan status',
            'directory',
            'plan year end',
            'active'
        ].includes(m.key)),
        {
            'key': 'plan id',
            'value': info.internalPlanId
        },
        {
            'key': 'plan name',
            'value': info.planName
        },
        {
            'key': 'plan year end',
            'value': info.planYearEnd
        },
        {
            'key': 'plan status',
            'value': info.active ? 'Active' : 'Deactivated'
        },
        {
            'key': 'directory',
            'value': info.directory
        },
        {
            'key': 'provider',
            'value': info.account.provider
        },
        {
            'key': 'account',
            'value': info.account.number
        },
        {
            'key': 'payee',
            'value': info.account.payee
        },
        ].filter(element => element.value)


        // Did we create or update the account? 
        if (this.state.editAccount) {

            // If we already have an _id, then we are editing the account... 
            if (info?.account?._id) {
                const res = await api.post("/tpa/account/update", {
                    id: info.account._id,
                    accountDiff: {
                        ...info.account
                    }
                })

                if (res.success) {
                    toast.success("Updated account information")
                }
                else
                    toast.error("Failed to update the account information")
            } else {
                // We are creating a new account...
                const res = await api.post("/tpa/account/create", {
                    account: {
                        internalPlanId: info.internalPlanId,
                        number: info.account.number,
                        payee: info.account.payee ?? "",
                        provider: info.account.provider,
                        planId: info._id,
                        ...(info.pensionProId ? { pensionProPlanId: info.pensionProId } : {})
                    }
                })

                if (res.success) {
                    toast.success("Created a new account!")
                }
                else
                    toast.error("Failed to create the new account")
            }

        }

        // Attatch the metadata to the doc. 
        await this.props.updateDocument(this.props.document._id, { metadata });

        if (this.props.user?.email?.endsWith("@stax.ai")) {
            // Potentially a Stax.ai admin team member - record action
            await api.post('/internal/record', {
                user: this.props.user.email,
                team: this.props.team.name,
                code: "Attach",
                points: 3,
                document: this.props.document._id,
                provider: info.account.provider,
                account: info.account.number
            });
        }

        this.props.modal({});
        this.props.loaded();
    }

    renderContent = () => {

        const Option = ({ children, ...props }) => (
            <components.Option {...props}>
                <div
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                    title={children}
                >
                    {children}
                </div>
            </components.Option>
        );


        return (
            <div className="modal-content modal-center">
                <h1>Attach account information</h1>
                <p>
                    Easily add your account details to this document. You'll be able to include essential information such as your internal plan ID, plan name, account number, payee, and provider name. Don't have an account yet? No worries! Quickly set one up by clicking the "Create Account" button.
                </p>
                <br />

                <div
                    className='config-form'
                    style={{ width: 800, position: 'relative', 'left': -20 }}
                >
                    <h2>{this.state.editAccount ? "Search for a plan" : "Search for an account"}</h2>
                    <input
                        className=''
                        style={{ width: 300 }}
                        type='text'
                        placeholder={this.state.editAccount ? "Search for a plan" : "Search for an account"}
                        value={this.state.query}
                        onChange={async (e) => {
                            this.setState({ query: e.target.value });
                            this.setState({ accounts: await this.searchAccounts(e.target.value, this.state.editAccount) });
                        }}
                        disabled={this.state.selected?.account?._id && this.state.editAccount ? true : false}
                    />

                    {this.state.editAccount && <>
                    
                        <div className='title-back'
                            style={{
                                position: 'absolute',
                                left: 20,
                                top: 20,
                                lineHeight: '27px',
                                backgroundPositionY: 'center'
                            }}
                            onClick={async () => {

                                const editAccount = !this.state.editAccount;
                                const query = editAccount ? "" : this.props.data.selection

                                this.setState({ editAccount, query });
                                const selected = {
                                    ...(editAccount ? {
                                        account: {
                                            number: this.props.data.selection
                                        }
                                    } : {})
                                }

                                const accounts = await this.searchAccounts(query, editAccount);
                                this.setState({
                                    selected,
                                    accounts
                                });
                            }}
                        >Back</div>
                    </>}

                    <div
                        style={{
                            textAlign: 'left',
                        }}
                    >
                        <Sortable
                            className="plan-editor-accounts-list"
                            title={null}
                            data={this.state.showMinimal ? this.state.accounts.slice(0, 3) : this.state.accounts}
                            scrollbar={false}
                            disableSort={true}
                            emptyMessage={this.state.editAccount ? "Could not find a plan, create one using the plan editor..." : "Could not find an account, try creating it..."}
                            loading={this.state.loading}

                            // Header configuration
                            header={{
                                name: {
                                    prop: "planId",
                                    label: "Plan ID",
                                    width: this.state.editAccount? 165:90
                                },
                                left: this.state.editAccount ?
                                    [
                                        {
                                            label: "Plan Name",
                                            prop: "Plan Name",
                                            width: 425
                                        },
                                        {
                                            prop: "active",
                                            label: "Active",
                                            width: 75
                                        },
                                        {
                                            prop: "Period End",
                                            label: "Period End",
                                            width: 100
                                        }
                                    ]
                                    :
                                    [
                                        {
                                            label: "Plan Name",
                                            prop: "label",
                                            width: 260
                                        },
                                        {
                                            prop: "provider",
                                            label: "Provider",
                                            width: 200
                                        },
                                        {
                                            prop: "accountNumber",
                                            label: "Account Number",
                                            width: 150
                                        }
                                    ],
                                meta: [
                                    {
                                        prop: "",
                                        label: "",
                                        width: this.state.editAccount? 0: 30
                                    }
                                ],
                            }}
                            renderItem={(item, idx) => {

                                const { columnA, columnB, columnC, columnD } =
                                {
                                    ...(this.state.editAccount ?
                                        {
                                            columnA: item?.internalPlanId, // plan id
                                            columnB: item?.planName, // plan name
                                            columnC: item?.active ? "Active" : "Terminated", // active
                                            columnD: `${item?.endMm}/${item?.endDd}`, // period end
                                        }
                                        :
                                        {
                                            columnA: item?.internalPlanId, //plan id
                                            columnB: item?.planName, // planName
                                            columnC: item?.account?.provider, // provider
                                            columnD: item?.account?.number, // Account Number
                                        })
                                }

                                const isSelected = this.state.editAccount ?
                                    this.state.selected?.internalPlanId === item?.internalPlanId
                                    :
                                    this.state.selected?.account?._id === item?.account?._id;

                                return (
                                    <div
                                        className={"attach-info-card " + (isSelected ? "selected" : "")}
                                        onClick={() => {
                                            const account = { ...this.state.selected?.account };

                                            const update = {
                                                ...item,
                                                ...(this.state.editAccount ? {
                                                    account: account
                                                } : {})
                                            }
                                            this.setState({ selected: update });
                                        }}
                                    >

                                        <div
                                            className='attach-info-card-detail columnA'
                                            style={{
                                                width: this.state.editAccount ? 145 : 70
                                            }}
                                        >{columnA}</div>
                                        <div className='attach-info-card-detail columnB'
                                            style={{
                                                width: this.state.editAccount ? 405 : 240
                                            }}
                                        >{columnB}</div>
                                        <div className='attach-info-card-detail columnC'
                                            style={{
                                                width: this.state.editAccount ? 55 : 180
                                            }}
                                        >{columnC}</div>
                                        <div
                                            className='attach-info-card-detail columnD'
                                            style={{
                                                width: this.state.editAccount ? 80 : 130
                                            }}
                                        >{columnD}</div>

                                        {
                                            !this.state.editAccount &&
                                            <div
                                                className={`attach-info-card-detail a edit`}
                                                onClick={() => {

                                                    const editAccount = true;
                                                    const selected = { ...item }
                                                    const accounts = [item];

                                                    this.setState({
                                                        editAccount,
                                                        selected,
                                                        accounts
                                                    });
                                                }}
                                            >
                                                Edit
                                            </div>
                                        }
                                    </div>
                                )
                            }}
                        />

                        {
                            this.state.accounts?.length > 3 ?
                                <div
                                    className='meta-showmore'
                                    style={{ paddingLeft: 0 }}
                                    onClick={() => {
                                        this.setState({
                                            showMinimal: !this.state.showMinimal
                                        })
                                    }}
                                >
                                    {this.state.editAccount ? this.state.showMinimal ? "Show all plans..." : "Collapse plans..." : this.state.showMinimal ? "Show all accounts..." : "Collapse accounts..."}
                                </div> : ""
                        }
                    </div>
                    
                    {
                        !this.state.editAccount &&
                        <div
                            className='a'
                            style={{ marginBottom: 40 }}
                            onClick={async () => {

                                const editAccount = !this.state.editAccount;
                                const query = editAccount ? "" : this.props.data.selection

                                this.setState({ editAccount, query });
                                const selected = {
                                    ...(editAccount ? {
                                        account: {
                                            number: this.props.data.selection
                                        }
                                    } : {})
                                }

                                const accounts = await this.searchAccounts(query, editAccount);
                                this.setState({
                                    selected,
                                    accounts
                                });
                            }}
                        >
                            {this.state.editAccount ? "Search for an account" : "Create a new account"}
                        </div>
                    }


                    {
                        // Are we editing/creating an account? Add the configurable fields here... 
                        this.state.editAccount &&
                        <>
                            <br />

                            <h2>Account Details</h2>

                            <div
                                className='config'
                                style={{
                                    margin: 'auto',
                                    width: 800,
                                }}
                            >
                                <label>Provider <span className='config-required'>(required)</span></label>
                                <CreatableSelect
                                    className="select-container"
                                    classNamePrefix='select'
                                    components={{ Option }}
                                    styles={{
                                        valueContainer: (base) => ({
                                            ...base,
                                            overflowX: 'auto',
                                        }),
                                        width: 800
                                    }}
                                    value={{
                                        value: this.state.selected?.account?.provider ?? "",
                                        label: this.state.selected?.account?.provider ?? ""
                                    }}
                                    options={this.state.providers?.map(elem => ({
                                        value: elem.DisplayName,
                                        label: elem.DisplayName
                                    }))}
                                    onChange={(e) => {
                                        this.setState({
                                            selected: {
                                                ...this.state.selected,
                                                account: {
                                                    ...this.state.selected.account,
                                                    provider: e.value
                                                }
                                            }
                                        })
                                    }}
                                    isSearchable={true}
                                    placeholder="Provider..."
                                />
                                <div style={{clear: 'both', height: 10}} />
                                <div
                                    className='hint'
                                >
                                    <p>What provider does this brokerage statement belong to?</p>
                                </div>
                            </div>
                            <div
                                className='config'
                                style={{
                                    width: 800,
                                    margin: 'auto'
                                }}
                            >
                                <label>Account Number <span className='config-required'>(required)</span></label>
                                <input
                                    type='text'
                                    className="config-field-string"
                                    value={this.state.selected?.account?.number}
                                    onChange={(e) => {
                                        this.setState({
                                            selected: {
                                                ...this.state.selected,
                                                account: {
                                                    ...this.state.selected.account,
                                                    number: e.target.value
                                                }
                                            }
                                        })
                                    }}
                                />
                                <div style={{clear: 'both', height: 10}} />
                                <div
                                    className='hint'
                                >
                                    <p>What's the account number shown on the statement</p>
                                </div>
                            </div>
                            <div
                                className='config'
                                style={{
                                    width: 800,
                                    margin: 'auto'
                                }}
                            >
                                <label>Payee</label>
                                <input
                                    type='text'
                                    className="config-field-string"
                                    value={this.state.selected?.account?.payee}
                                    onChange={(e) => {
                                        this.setState({
                                            selected: {
                                                ...this.state.selected,
                                                account: {
                                                    ...this.state.selected.account,
                                                    payee: e.target.value
                                                }
                                            }
                                        })
                                    }}
                                />
                                <div style={{clear: 'both', height: 10}} />
                                <div
                                    className='hint'
                                >
                                    <p>What's the name of the recipient shown on the statement</p>
                                </div>
                            </div>
                            <br />
                        </>
                    }
                </div>

                <br />

                <div
                    className="button"
                    style={{
                        float: 'none',
                        backgroundImage: "url('https://static.stax.ai/images/icons/reviewed.png')",
                        marginTop: 16
                    }}
                    onClick={this.handleSubmit}
                >
                    Attach account information
                </div>

            </div>
        );
    }

};